import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-paper dark:bg-noir py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-cream dark:bg-card rounded-2xl p-6 md:p-10 brutal-border brutal-shadow-lg">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sun text-ink font-display font-bold text-sm brutal-border-thin brutal-hover mb-8"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </a>

          <div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-ink dark:text-cream mb-2">Privacy Policy</h1>

            <p className="text-ink/60 dark:text-cream/60 mb-8 text-sm font-bold">
              Last Updated: September 7, 2025
            </p>
            
            <p className="text-ink/70 dark:text-cream/70 mb-6 leading-relaxed">
              Tushar Anand ("we," "us," or "our") operates the portfolio website (the "Service"). This page informs you of our policies regarding the collection, use, and disclosure of personal data when you use our Service and the choices you have associated with that data.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Information Collection and Use</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We collect several different types of information for various purposes to provide and improve our Service to you.
            </p>
            
            <h3 className="font-display text-xl font-extrabold text-ink dark:text-cream mt-6 mb-3">Types of Data Collected</h3>
            
            <h4 className="font-display text-lg font-extrabold text-ink dark:text-cream mt-4 mb-2">Personal Data</h4>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:
            </p>
            
            <ul className="list-disc pl-6 marker:text-mint text-ink/70 dark:text-cream/70 mb-4 space-y-2">
              <li>Email address</li>
              <li>First name and last name</li>
              <li>Phone number</li>
              <li>Any other information you choose to provide in the contact form</li>
            </ul>
            
            <h4 className="font-display text-lg font-extrabold text-ink dark:text-cream mt-4 mb-2">Usage Data</h4>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We may also collect information on how the Service is accessed and used ("Usage Data"). This Usage Data may include information such as:
            </p>
            
            <ul className="list-disc pl-6 marker:text-mint text-ink/70 dark:text-cream/70 mb-4 space-y-2">
              <li>Your computer's Internet Protocol address (e.g. IP address)</li>
              <li>Browser type and version</li>
              <li>The pages of our Service that you visit</li>
              <li>The time and date of your visit</li>
              <li>The time spent on those pages</li>
              <li>Unique device identifiers and other diagnostic data</li>
            </ul>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Use of Data</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              Tushar Anand uses the collected data for various purposes:
            </p>
            
            <ul className="list-disc pl-6 marker:text-mint text-ink/70 dark:text-cream/70 mb-4 space-y-2">
              <li>To provide and maintain our Service</li>
              <li>To notify you about changes to our Service</li>
              <li>To allow you to participate in interactive features of our Service when you choose to do so</li>
              <li>To provide customer support</li>
              <li>To gather analysis or valuable information so that we can improve our Service</li>
              <li>To monitor the usage of our Service</li>
              <li>To detect, prevent and address technical issues</li>
            </ul>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Security of Data</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Service Providers</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We may employ third party companies and individuals to facilitate our Service ("Service Providers"), to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used.
            </p>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Analytics</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We may use third-party Service Providers to monitor and analyze the use of our Service.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Links to Other Sites</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.
            </p>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Changes to This Privacy Policy</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
            </p>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              We will let you know via email and/or a prominent notice on our Service, prior to the change becoming effective and update the "last updated" date at the top of this Privacy Policy.
            </p>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
            
            <h2 className="font-display text-2xl font-extrabold text-ink dark:text-cream mt-10 mb-4 pt-6 border-t-2 border-ink/15 dark:border-cream/15">Contact Us</h2>
            
            <p className="text-ink/70 dark:text-cream/70 mb-4 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            
            <ul className="list-disc pl-6 marker:text-mint text-ink/70 dark:text-cream/70 mb-4 space-y-2">
              <li>By email: tushar.anand.bpmce@gmail.com</li>
              <li>By phone: +91-9060177959</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;